import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, RefreshCw, CheckCircle, XCircle, Clock, AlertTriangle, Wifi } from 'lucide-react';
import { API_BASE_URL, apiClient } from '../services/httpClient';

/**
 * Admin Debug Network Page
 * Displays network diagnostic information for debugging
 * Accessible only from More > Admin
 * 
 * ⭐ V3 iOS FIX: Uses API_BASE_URL from httpClient for correct iOS Capacitor resolution
 */

// Global error tracking - captured across the app
// This can be imported and used by API services
export const networkErrorTracker = {
  lastError: null,
  setError: (error) => {
    networkErrorTracker.lastError = error;
  },
  getError: () => networkErrorTracker.lastError,
  clear: () => { networkErrorTracker.lastError = null; }
};

// App version info
const APP_VERSION = '1.0';
const BUILD_NUMBER = '10';

const AdminDebugNetwork = ({ t }) => {
  const navigate = useNavigate();
  
  // State
  const [backendUrl, setBackendUrl] = useState('');
  const [healthStatus, setHealthStatus] = useState(null);
  const [isTestingHealth, setIsTestingHealth] = useState(false);
  const [lastApiError, setLastApiError] = useState(null);
  
  // Get backend URL on mount
  useEffect(() => {
    // ⭐ V3 iOS FIX: Use API_BASE_URL instead of process.env
    setBackendUrl(API_BASE_URL || 'undefined');
    console.log('[AdminDebug] API_BASE_URL:', API_BASE_URL);
    
    // Load last error from tracker
    setLastApiError(networkErrorTracker.getError());
  }, []);
  
  // Test health endpoint
  const testHealth = async () => {
    setIsTestingHealth(true);
    const startTime = performance.now();
    
    try {
      // ⭐ V3 iOS FIX: Use apiClient instead of fetch for correct URL resolution
      console.log('[AdminDebug] Testing health with API_BASE_URL:', API_BASE_URL);
      const response = await apiClient.get('/api/health');
      
      const endTime = performance.now();
      const latency = Math.round(endTime - startTime);
      
      // apiClient returns response.data directly with status in response
      setHealthStatus({
        status: 'OK',
        httpCode: response.status,
        message: response.data?.status || 'healthy',
        latency,
        timestamp: new Date().toISOString(),
        data: response.data
      });
    } catch (error) {
      const endTime = performance.now();
      const latency = Math.round(endTime - startTime);
      
      setHealthStatus({
        status: 'FAILED',
        httpCode: error.response?.status || null,
        message: error.message || 'Network error',
        latency,
        timestamp: new Date().toISOString(),
        errorType: error.name
      });
    } finally {
      setIsTestingHealth(false);
    }
  };
  
  // Auto-test on mount
  useEffect(() => {
    testHealth();
  }, []);
  
  return (
    <div className="min-h-screen bg-[#0A0F1C] pb-32 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-0 w-80 h-80 bg-amber-500/10 rounded-full blur-3xl" />
      </div>
      
      {/* Header */}
      <div className="sticky top-0 z-20 bg-[#0A0F1C]/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-3">
          <button 
            onClick={() => navigate('/profile')}
            className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-white" />
          </button>
          <div>
            <h1 className="text-lg font-semibold text-white">🔌 Debug réseau</h1>
            <p className="text-xs text-white/50">Diagnostic de connexion</p>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="relative z-10 max-w-lg mx-auto px-4 pt-6 space-y-4">
        
        {/* App Version */}
        <div className="p-4 rounded-xl bg-white/5 border border-white/10">
          <div className="flex items-center justify-between">
            <span className="text-white/60 text-sm">App Version</span>
            <span className="text-white font-mono text-sm">{APP_VERSION} ({BUILD_NUMBER})</span>
          </div>
        </div>
        
        {/* Backend URL */}
        <div className="p-4 rounded-xl bg-white/5 border border-white/10">
          <div className="flex items-center gap-2 mb-2">
            <Wifi className="w-4 h-4 text-white/60" />
            <span className="text-white/60 text-sm">Backend URL</span>
          </div>
          <p className={`font-mono text-sm break-all ${backendUrl === 'undefined' ? 'text-red-400' : 'text-green-400'}`}>
            {backendUrl}
          </p>
        </div>
        
        {/* Health Test */}
        <div className="p-4 rounded-xl bg-white/5 border border-white/10">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              {healthStatus?.status === 'OK' ? (
                <CheckCircle className="w-5 h-5 text-green-400" />
              ) : healthStatus?.status === 'FAILED' ? (
                <XCircle className="w-5 h-5 text-red-400" />
              ) : (
                <Clock className="w-5 h-5 text-white/40" />
              )}
              <span className="text-white font-medium">Health Check</span>
            </div>
            <button
              onClick={testHealth}
              disabled={isTestingHealth}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-orange-500/20 hover:bg-orange-500/30 border border-orange-500/30 text-orange-300 text-sm transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${isTestingHealth ? 'animate-spin' : ''}`} />
              Re-tester
            </button>
          </div>
          
          {healthStatus && (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-white/50">Status</span>
                <span className={healthStatus.status === 'OK' ? 'text-green-400 font-medium' : 'text-red-400 font-medium'}>
                  {healthStatus.status}
                </span>
              </div>
              {healthStatus.httpCode && (
                <div className="flex justify-between">
                  <span className="text-white/50">HTTP Code</span>
                  <span className="text-white font-mono">{healthStatus.httpCode}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-white/50">Message</span>
                <span className={`font-mono ${healthStatus.status === 'OK' ? 'text-white/80' : 'text-red-300'}`}>
                  {healthStatus.message}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/50">Latency</span>
                <span className="text-white font-mono">{healthStatus.latency} ms</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/50">Last test</span>
                <span className="text-white/60 text-xs">
                  {new Date(healthStatus.timestamp).toLocaleString()}
                </span>
              </div>
            </div>
          )}
        </div>
        
        {/* Last API Error */}
        <div className="p-4 rounded-xl bg-white/5 border border-white/10">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-yellow-400" />
            <span className="text-white font-medium">Last API Error</span>
          </div>
          
          {lastApiError ? (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-white/50">Method</span>
                <span className="text-white font-mono">{lastApiError.method || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/50">Endpoint</span>
                <span className="text-white/80 font-mono text-xs break-all">{lastApiError.endpoint || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/50">Message</span>
                <span className="text-red-300 font-mono text-xs">{lastApiError.message || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/50">Timestamp</span>
                <span className="text-white/60 text-xs">
                  {lastApiError.timestamp ? new Date(lastApiError.timestamp).toLocaleString() : 'N/A'}
                </span>
              </div>
            </div>
          ) : (
            <p className="text-green-400 text-sm">✓ None</p>
          )}
        </div>
        
        {/* Environment Info */}
        <div className="p-4 rounded-xl bg-white/5 border border-white/10">
          <h3 className="text-white font-medium mb-3">Environment</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-white/50">NODE_ENV</span>
              <span className="text-white font-mono">{process.env.NODE_ENV || 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-white/50">User Agent</span>
              <span className="text-white/60 text-xs break-all">{navigator.userAgent.substring(0, 50)}...</span>
            </div>
            <div className="flex justify-between">
              <span className="text-white/50">Platform</span>
              <span className="text-white font-mono">{navigator.platform || 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-white/50">Online</span>
              <span className={navigator.onLine ? 'text-green-400' : 'text-red-400'}>
                {navigator.onLine ? 'Yes' : 'No'}
              </span>
            </div>
          </div>
        </div>
        
        {/* Clear error button */}
        {lastApiError && (
          <button
            onClick={() => {
              networkErrorTracker.clear();
              setLastApiError(null);
            }}
            className="w-full py-3 rounded-xl bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 text-red-300 text-sm transition-colors"
          >
            Clear Last Error
          </button>
        )}
        
      </div>
    </div>
  );
};

export default AdminDebugNetwork;
